// =============================================================================
// Till preview door: the SCREENS (special-card config, Phase 6, 28 Sep 2026).
//
// Inside the Command Centre's card builder, <host>/?preview=till draws the REAL,
// UNMODIFIED till screens for one card type, from what the builder sends:
//   scan             the scan result staff get for this card (CCStampResult)
//   offline          the offline picker's card tiles (CCOfflineRedeemPicker)
//   offline_result   the offline result screen for this card (CCOfflineRedeemResult)
//   lookup           Customer Lookup, with a stand-in customer holding this card
//   lookup_redeemed  the same, after tapping Redeem: the "Redeemed" pop-up
// The builder's data is the backend's till-preview answer: the card-type list, the
// scan reply, the Lookup reply and the Lookup redeem reply, in the real routes'
// shapes, made from the unsaved draft by the real shaping code.
//
// lib/tillPreviewGuard.js (main.jsx's first import) has already closed the page off:
// in-memory storage, no IndexedDB, every fetch answered from the builder's data, no
// beacon / XHR / socket, no focus stealing. The offline picker reads the card-type
// list from (in-memory) storage exactly as a tablet reads its cached copy. Lookup is
// reached the way staff reach it: typing the stand-in's code into the real search
// box and tapping the real buttons, inside this frame only. No screen code differs
// from the till's.
// =============================================================================
import React from "react";
import * as ReactDOM from "react-dom";
import { getActiveSite } from "./config/sites.js";
import { CCStampResult, CCLookup, CCOfflineRedeemPicker, CCOfflineRedeemResult, CCScannerDefs, CC_CARDTYPES_CACHE, CC_TIERREWARDS_CACHE } from "./foh.jsx";
import { tillPreviewWorld, subscribeTillPreview, makeMemoryStorage, tellTillPreviewBuilder } from "./lib/tillPreviewGuard.js";
import { previewVisibleMarkers, previewPageQuiet } from "./lib/previewProbe.js";

const { useEffect, useState } = React;
const noop = () => {};

// Resolves with test()'s first truthy answer, checking every 30 ms for at most `ms`.
// Resolves null early if a newer message has replaced this run.
function waitFor(test, run, ms) {
  return new Promise((resolve) => {
    const t0 = Date.now();
    const tick = () => {
      if (!tillPreviewWorld.msg || tillPreviewWorld.msg.run !== run) return resolve(null);
      let v = null;
      try { v = test(); } catch (e) { v = null; }
      if (v) return resolve(v);
      if (Date.now() - t0 > ms) return resolve(null);
      setTimeout(tick, 30);
    };
    tick();
  });
}

// Types into a React-controlled input the way a keyboard does (React hears "input").
function typeInto(input, value) {
  const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value").set;
  setter.call(input, value);
  input.dispatchEvent(new Event("input", { bubbles: true }));
}

// Brings `el` to the top of whichever box in THIS frame scrolls it. Never
// scrollIntoView, which can scroll the builder page around the frame too.
function scrollToTop(el) {
  let p = el.parentElement;
  while (p && p !== document.body && p !== document.documentElement) {
    const cs = window.getComputedStyle(p);
    if (/(auto|scroll)/.test(cs.overflowY) && p.scrollHeight > p.clientHeight) {
      p.scrollTop = Math.max(0, el.getBoundingClientRect().top - p.getBoundingClientRect().top + p.scrollTop - 16);
      return;
    }
    p = p.parentElement;
  }
  window.scrollTo(0, Math.max(0, el.getBoundingClientRect().top + window.pageYOffset - 16));
}

// Drives the real Customer Lookup to the list (and, for lookup_redeemed, the pop-up).
// Answers true, or a plain line saying what the real screen doesn't have.
async function driveLookup(msg, root) {
  const lookup = msg.data.lookup || {};
  const suffix = String(lookup.loyalty_id || "").toUpperCase().replace(/^ROLL-?/, "").replace(/[^A-Z0-9]/g, "").slice(0, 6);
  const input = await waitFor(() => root.querySelector(".cc-lookup-search input"), msg.run, 3000);
  if (!input || !suffix) return false;
  typeInto(input, suffix);
  const go = await waitFor(() => { const b = root.querySelector(".cc-lookup-search button"); return b && !b.disabled ? b : null; }, msg.run, 3000);
  if (!go) return false;
  go.click();
  const cards = Array.isArray(lookup.special_cards) ? lookup.special_cards : [];
  const i = cards.findIndex((c) => c && (c.card_type || c.key) === msg.cardType);
  const row = await waitFor(() => { const rows = root.querySelectorAll(".cc-lookup-cardrow"); return (i >= 0 && rows.length === cards.length) ? rows[i] : null; }, msg.run, 3000);
  if (!row) return false;
  if (msg.view === "lookup") return true;
  const redeem = row.querySelector("button");
  if (!redeem) return "This card has no Redeem button in Customer Lookup.";
  redeem.click();
  return !!(await waitFor(() => /Redeemed · click in Square/.test(root.textContent || ""), msg.run, 3000));
}

// Scrolls the offline picker to this card's tile (tiles follow the card-type list's order).
async function driveOffline(msg, root) {
  const types = Array.isArray(msg.data.types) ? msg.data.types : [];
  const i = types.findIndex((t) => t && t.key === msg.cardType);
  const tile = await waitFor(() => { const g = root.querySelector(".cc-pick-grouplab--gap + .cc-pick-cardgrid"); return (g && i >= 0) ? g.children[i] : null; }, msg.run, 3000);
  if (!tile) return false;
  scrollToTop(tile);
  return true;
}

function TillPreviewRun({ msg }) {
  const d = msg.data;
  // Fresh storage for every run, seeded with exactly what an online tablet caches for
  // the offline picker (its card-type list and tier-reward list).
  useState(() => {
    tillPreviewWorld.local = makeMemoryStorage({
      [CC_CARDTYPES_CACHE]: JSON.stringify(Array.isArray(d.types) ? d.types : []),
      [CC_TIERREWARDS_CACHE]: JSON.stringify(Array.isArray(d.tier_rewards) ? d.tier_rewards : []),
    });
    tillPreviewWorld.session = makeMemoryStorage(null);
    return true;
  });
  useEffect(() => {
    const root = document.getElementById("root");
    const drive = (msg.view === "lookup" || msg.view === "lookup_redeemed") ? driveLookup(msg, root)
      : msg.view === "offline" ? driveOffline(msg, root)
      : Promise.resolve(true);
    // "rendered" as the builder's other doors send it: reached:false (and a plain
    // line when there is one) when the screen asked for couldn't be drawn.
    drive.then((ok) => {
      if (!tillPreviewWorld.msg || tillPreviewWorld.msg.run !== msg.run) return;
      const out = { type: "rolled-till-preview-rendered", rev: msg.rev, view: msg.view, reached: ok === true };
      if (typeof ok === "string") out.line = ok;
      // A probe copy (the builder's highlight): answer once the drawn screen has settled,
      // naming which of the builder's own markers are on it.
      if (msg.probe && ok === true) {
        previewPageQuiet(400, 6000).then(() => {
          if (!tillPreviewWorld.msg || tillPreviewWorld.msg.run !== msg.run) return;
          out.visible = previewVisibleMarkers(msg.probe);
          tellTillPreviewBuilder(out);
        });
        return;
      }
      requestAnimationFrame(() => tellTillPreviewBuilder(out));
    });
  }, []);
  // The session a PIN login gives the scanner, minus the staff member: only the
  // site's name is ever drawn (the Lookup header).
  const site = getActiveSite() || {};
  const session = { staffId: null, staffName: "", staffRole: "staff", siteId: site.id || null, siteName: site.name || "" };
  let body = null;
  if (msg.view === "scan") {
    body = (d.scan && d.scan.redemption) ? <CCStampResult result={d.scan} onDone={noop} /> : null;
  } else if (msg.view === "offline") {
    body = <CCOfflineRedeemPicker loyaltyId={(d.lookup && d.lookup.loyalty_id) || ""} staffName="" session={session} onDone={noop} />;
  } else if (msg.view === "offline_result") {
    const t = (Array.isArray(d.types) ? d.types : []).find((x) => x && x.key === msg.cardType);
    body = t ? <CCOfflineRedeemResult picked={{ kind: "special", ref: t.key, obj: t }} onDone={noop} /> : null;
  } else {
    body = <CCLookup session={session} token="till-preview" requestPin={noop} onCompStamp={noop} onMigrate={noop} onBack={noop} showToast={noop} />;
  }
  // The same root the scanner draws every screen in (ClubCardScanner).
  return <div className="cc-root"><CCScannerDefs />{body}</div>;
}

function TillPreview() {
  const [msg, setMsg] = useState(() => tillPreviewWorld.msg);
  useEffect(() => {
    const off = subscribeTillPreview(setMsg);
    if (tillPreviewWorld.msg) setMsg(tillPreviewWorld.msg);
    return off;
  }, []);
  // Nothing until the builder sends a card: plain nori. Nothing at all if the page
  // couldn't be closed off.
  if (!msg || !tillPreviewWorld.ok) return <div style={{ minHeight: "100vh", background: "#00392C" }} />;
  return <TillPreviewRun key={"run-" + msg.run} msg={msg} />;
}

export function mountTillPreview(root) {
  ReactDOM.render(<TillPreview />, root);
  // Tell the builder we're listening; it (re)sends the latest card. No data.
  tellTillPreviewBuilder({ type: "rolled-till-preview-ready" });
}
